import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Search, Upload, Save, X, Loader, Clock, AlertCircle } from 'lucide-react'
import * as XLSX from 'xlsx'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { getProducts, updateProductPrice, bulkUpdatePrices, migrateAllProductPrices } from '../../data/products'
import { getDepartmentBranch, getProductPrice, migrateProductPrices } from '../../lib/productPricing'

const DEPTS = ['kitchen', 'bakery', 'yawehs', 'tuckshop']
const BRANCH_LABELS = { limpopo: 'Limpopo', pretoria: 'Pretoria' }

function fmtR(v) {
  if (v == null) return '—'
  const n = Number(v)
  if (!Number.isFinite(n) || n === 0) return '—'
  return `R${n.toFixed(2)}`
}

function fmtDate(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return '—' }
}

function collectAllProducts() {
  const all = []
  for (const d of DEPTS) {
    for (const p of getProducts(d)) {
      all.push({ ...p, department: p.department || d })
    }
  }
  return all
}

// Build a view row combining persisted prices and the migration fallback
// so the UI always shows a coherent picture for un-migrated products.
function toRowView(p) {
  const migrated = migrateProductPrices([p])[0]
  return {
    ...p,
    prices: migrated.prices, // may be the original reference if already migrated
  }
}

export default function PriceBookTab() {
  const { user } = useAuth()
  const { addToast } = useToast()
  const adminEmail = user?.email || 'unknown'

  const [refreshKey, setRefreshKey] = useState(0)
  const [branchView, setBranchView] = useState('both') // 'both' | 'limpopo' | 'pretoria'
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [editing, setEditing] = useState(null) // { code, department, branch }
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [migrating, setMigrating] = useState(false)
  const [migrationDone, setMigrationDone] = useState(false)
  const [showBulk, setShowBulk] = useState(false)
  const [historyFor, setHistoryFor] = useState(null) // product row

  // Run one-shot migration on first mount. Non-blocking for the UI — the
  // table renders from migrateProductPrices() view-mode while the persist
  // is in flight, so the display is correct even if the batch hasn't
  // completed yet.
  useEffect(() => {
    let cancelled = false
    async function migrate() {
      setMigrating(true)
      try {
        const res = await migrateAllProductPrices()
        if (!cancelled) {
          if (res.migrated > 0) addToast(`Migrated prices for ${res.migrated} products`, 'success')
          setMigrationDone(true)
          setRefreshKey(k => k + 1)
        }
      } catch (err) {
        console.warn('[PriceBook] migration failed:', err?.message)
        Sentry.captureException(err, { extra: { fn: 'PriceBookTab.migrate' } })
      } finally {
        if (!cancelled) setMigrating(false)
      }
    }
    migrate()
    return () => { cancelled = true }
  }, [])

  const allRows = useMemo(() => {
    const raw = collectAllProducts()
    return raw.map(toRowView).sort((a, b) => {
      const cat = String(a.category || '').localeCompare(String(b.category || ''))
      return cat !== 0 ? cat : String(a.name || '').localeCompare(String(b.name || ''))
    })
  }, [refreshKey])

  const categories = useMemo(() => {
    const s = new Set()
    for (const r of allRows) if (r.category) s.add(r.category)
    return ['all', ...Array.from(s).sort()]
  }, [allRows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return allRows.filter(r => {
      if (branchView !== 'both') {
        const branchOK = r.prices?.[branchView] != null
        if (!branchOK) return false
      }
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false
      if (q && !String(r.name).toLowerCase().includes(q) && !String(r.code).toLowerCase().includes(q)) return false
      return true
    })
  }, [allRows, branchView, categoryFilter, search])

  const openEdit = (row, branch) => {
    const current = row.prices?.[branch]
    setEditing({ code: row.code, department: row.department, branch })
    setEditValue(current == null ? '' : String(current))
  }

  const cancelEdit = () => { setEditing(null); setEditValue('') }

  const saveEdit = async () => {
    if (!editing) return
    const trimmed = editValue.trim()
    const nextVal = trimmed === '' ? null : Number(trimmed)
    if (trimmed !== '' && (!Number.isFinite(nextVal) || nextVal < 0)) {
      addToast('Enter a valid price (or leave blank for not-sold)', 'error')
      return
    }
    setSaving(true)
    try {
      const { oldPrice, newPrice } = await updateProductPrice(
        editing.code, editing.department, editing.branch, nextVal, adminEmail
      )
      const branchLabel = BRANCH_LABELS[editing.branch]
      const fmtOld = oldPrice == null ? '—' : `R${Number(oldPrice).toFixed(2)}`
      const fmtNew = newPrice == null ? '—' : `R${Number(newPrice).toFixed(2)}`
      addToast(`${editing.code} ${branchLabel}: ${fmtOld} → ${fmtNew}`, 'success')
      cancelEdit()
      setRefreshKey(k => k + 1)
    } catch (err) {
      addToast(`Update failed: ${err.message}`, 'error')
      Sentry.captureException(err, { extra: { fn: 'PriceBookTab.saveEdit', ...editing } })
    } finally {
      setSaving(false)
    }
  }

  const handleKey = (e) => {
    if (e.key === 'Enter') saveEdit()
    else if (e.key === 'Escape') cancelEdit()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-white">Price Book</h2>
        <div className="flex items-center gap-2">
          {migrating && <span className="text-xs text-gray-400 flex items-center gap-1"><Loader size={12} className="animate-spin" /> Migrating…</span>}
          <button
            onClick={() => setShowBulk(true)}
            className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-sm"
          >
            <Upload size={14} /> Upload Price List
          </button>
        </div>
      </div>

      {/* Branch selector */}
      <div className="flex gap-1">
        {['both', 'limpopo', 'pretoria'].map(v => (
          <button
            key={v}
            onClick={() => setBranchView(v)}
            className={`px-4 py-2 rounded-lg text-sm capitalize ${
              branchView === v ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {v === 'both' ? '↔ Both Branches' : v === 'limpopo' ? '🏔 Limpopo' : '🏙 Pretoria'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search product or code…"
            className="bg-gray-800 border border-gray-700 rounded pl-9 pr-3 py-2 text-sm text-white w-full"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-300 capitalize"
        >
          {categories.map(c => (
            <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>
          ))}
        </select>
        <span className="text-xs text-gray-500 ml-auto">{filteredRows.length} product{filteredRows.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Code</th>
              <th className="text-left py-2 px-2">Product</th>
              <th className="text-left py-2 px-2">Category</th>
              {(branchView === 'both' || branchView === 'limpopo') && (
                <th className="text-right py-2 px-2">Limpopo</th>
              )}
              {(branchView === 'both' || branchView === 'pretoria') && (
                <th className="text-right py-2 px-2">Pretoria</th>
              )}
              <th className="text-left py-2 px-2">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(row => {
              const lim = row.prices?.limpopo
              const pre = row.prices?.pretoria
              const differ = branchView === 'both' && lim != null && pre != null && Math.abs(Number(lim) - Number(pre)) > 0.01
              return (
                <tr key={`${row.department}_${row.code}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="py-2 px-2 font-mono text-xs">{row.code}</td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => setHistoryFor(row)}
                      className="text-left hover:text-blue-400 flex items-center gap-1"
                      title="View price history"
                    >
                      {row.name}
                      {(row.priceHistory?.length || 0) > 0 && (
                        <Clock size={10} className="text-gray-500" />
                      )}
                    </button>
                  </td>
                  <td className="py-2 px-2 capitalize text-gray-400">{row.category}</td>
                  {(branchView === 'both' || branchView === 'limpopo') && (
                    <PriceCell
                      row={row} branch="limpopo" value={lim}
                      editing={editing} editValue={editValue} setEditValue={setEditValue}
                      onOpen={() => openEdit(row, 'limpopo')}
                      onSave={saveEdit} onCancel={cancelEdit} onKey={handleKey}
                      saving={saving}
                      highlight={differ}
                    />
                  )}
                  {(branchView === 'both' || branchView === 'pretoria') && (
                    <PriceCell
                      row={row} branch="pretoria" value={pre}
                      editing={editing} editValue={editValue} setEditValue={setEditValue}
                      onOpen={() => openEdit(row, 'pretoria')}
                      onSave={saveEdit} onCancel={cancelEdit} onKey={handleKey}
                      saving={saving}
                      highlight={differ}
                    />
                  )}
                  <td className="py-2 px-2 text-xs text-gray-500">{fmtDate(row.updatedAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <p className="text-center py-8 text-sm text-gray-500">No products match these filters.</p>
        )}
      </div>

      {showBulk && (
        <BulkPriceModal
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); setRefreshKey(k => k + 1) }}
          adminEmail={adminEmail}
          addToast={addToast}
        />
      )}

      {historyFor && (
        <PriceHistoryDrawer
          product={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  )
}

function PriceCell({ row, branch, value, editing, editValue, setEditValue, onOpen, onSave, onCancel, onKey, saving, highlight }) {
  const isEditingThis = editing && editing.code === row.code && editing.department === row.department && editing.branch === branch
  const cellClass = highlight
    ? 'bg-amber-950/30 text-amber-200'
    : value == null
      ? 'text-gray-600'
      : 'text-gray-200'

  if (isEditingThis) {
    return (
      <td className="py-2 px-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <input
            autoFocus
            type="number" min="0" step="0.5"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={onKey}
            placeholder="— blank for not sold"
            className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-24 text-right"
          />
          <button onClick={onSave} disabled={saving} className="text-green-400 hover:text-green-300 disabled:opacity-50"><Save size={12} /></button>
          <button onClick={onCancel} disabled={saving} className="text-gray-500 hover:text-gray-300 disabled:opacity-50"><X size={12} /></button>
        </div>
      </td>
    )
  }
  return (
    <td
      className={`py-2 px-2 text-right cursor-pointer hover:bg-gray-800/50 ${cellClass}`}
      onClick={onOpen}
      title="Click to edit"
    >
      {fmtR(value)}
    </td>
  )
}

function PriceHistoryDrawer({ product, onClose }) {
  const history = product.priceHistory || []
  const byBranch = { limpopo: [], pretoria: [] }
  for (const h of history) {
    if (byBranch[h.branch]) byBranch[h.branch].unshift(h) // newest first
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose}>
      <div
        className="bg-gray-900 border-l border-gray-800 w-full max-w-md h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div>
            <h3 className="text-sm font-semibold text-white">{product.name}</h3>
            <p className="text-xs text-gray-500">Price History</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-5">
          {['limpopo', 'pretoria'].map(br => (
            <div key={br}>
              <h4 className="text-xs font-medium text-brand-400 uppercase tracking-wider mb-2">{BRANCH_LABELS[br]}</h4>
              {byBranch[br].length === 0 ? (
                <p className="text-xs text-gray-600 italic">No history — using seed price</p>
              ) : (
                <ul className="space-y-2">
                  {byBranch[br].map((h, i) => (
                    <li key={i} className="text-xs text-gray-300 bg-gray-800/50 rounded px-3 py-2">
                      <span className="font-medium">{fmtR(h.fromPrice)} → {fmtR(h.toPrice)}</span>
                      <span className="text-gray-500 block mt-0.5">{fmtDate(h.changedAt)} · {h.changedBy}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Bulk Price Upload Modal ──────────────────────────────────────────

function BulkPriceModal({ onClose, onDone, adminEmail, addToast }) {
  const [rows, setRows] = useState([]) // parsed preview rows
  const [saving, setSaving] = useState(false)

  const allProducts = useMemo(() => {
    const m = new Map()
    for (const d of DEPTS) for (const p of getProducts(d)) {
      m.set(String(p.code).toLowerCase(), { ...p, department: p.department || d })
    }
    return m
  }, [])

  const handleFile = async (file) => {
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array' })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
      const parsed = []
      for (let i = 0; i < data.length; i++) {
        const r = data[i]
        if (!r || r.length === 0) continue
        const [rawCode, rawLim, rawPre] = r
        const code = String(rawCode || '').trim()
        if (!code) continue
        if (i === 0 && /code/i.test(code)) continue // skip header
        const prod = allProducts.get(code.toLowerCase())
        if (!prod) {
          parsed.push({ code, name: '(not found)', department: null, error: 'not found' })
          continue
        }
        const currentLim = prod.prices?.limpopo ?? (getDepartmentBranch(prod.department) === 'limpopo' ? prod.unitPrice ?? null : null)
        const currentPre = prod.prices?.pretoria ?? (getDepartmentBranch(prod.department) === 'pretoria' ? prod.unitPrice ?? null : null)
        const newLim = rawLim === '' || rawLim == null ? currentLim : Number(rawLim)
        const newPre = rawPre === '' || rawPre == null ? currentPre : Number(rawPre)
        parsed.push({
          code: prod.code, name: prod.name, department: prod.department,
          currentLim, currentPre, newLim, newPre,
        })
      }
      setRows(parsed)
    } catch (err) {
      addToast('Failed to parse file — use Code, Limpopo, Pretoria columns', 'error')
      Sentry.captureException(err, { extra: { fn: 'PriceBook.bulk.parse' } })
    }
  }

  const changeSummary = (row) => {
    const parts = []
    if (row.department && row.currentLim !== row.newLim && Math.abs(Number(row.currentLim ?? 0) - Number(row.newLim ?? 0)) > 0.01) {
      const diff = Number(row.newLim ?? 0) - Number(row.currentLim ?? 0)
      parts.push(`Limpopo ${diff > 0 ? '+' : ''}R${diff.toFixed(2)}`)
    }
    if (row.department && row.currentPre !== row.newPre && Math.abs(Number(row.currentPre ?? 0) - Number(row.newPre ?? 0)) > 0.01) {
      const diff = Number(row.newPre ?? 0) - Number(row.currentPre ?? 0)
      parts.push(`Pretoria ${diff > 0 ? '+' : ''}R${diff.toFixed(2)}`)
    }
    return parts.length ? parts.join(' · ') : '—'
  }

  const handleSave = async () => {
    const updates = []
    for (const r of rows) {
      if (!r.department || r.error) continue
      if (Math.abs(Number(r.currentLim ?? 0) - Number(r.newLim ?? 0)) > 0.01) {
        updates.push({ code: r.code, department: r.department, branch: 'limpopo', newPrice: r.newLim })
      }
      if (Math.abs(Number(r.currentPre ?? 0) - Number(r.newPre ?? 0)) > 0.01) {
        updates.push({ code: r.code, department: r.department, branch: 'pretoria', newPrice: r.newPre })
      }
    }
    if (updates.length === 0) {
      addToast('No price changes to save', 'info')
      return
    }
    setSaving(true)
    try {
      const res = await bulkUpdatePrices(updates, adminEmail)
      addToast(`Updated ${res.updated} · Unchanged ${res.unchanged}${res.failed.length ? ` · Failed ${res.failed.length}` : ''}`, 'success')
      onDone()
    } catch (err) {
      addToast(`Bulk update failed: ${err.message}`, 'error')
      Sentry.captureException(err, { extra: { fn: 'PriceBook.bulk.save' } })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-white">Upload Price List</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-gray-400">
            Expected columns: <code className="text-brand-400">Code, Limpopo Price, Pretoria Price</code>.
            Leave a cell blank to keep the current price.
          </p>
          <input
            type="file" accept=".csv,.xlsx,.xls"
            onChange={e => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
            }}
            className="text-xs text-gray-300"
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          />
          <label
            className="inline-flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 px-3 py-1.5 rounded-lg text-sm cursor-pointer"
          >
            <Upload size={14} /> Choose file…
            <input
              type="file" accept=".csv,.xlsx,.xls"
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) handleFile(f)
              }}
              style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
            />
          </label>

          {rows.length > 0 && (
            <div className="overflow-x-auto border border-gray-800 rounded mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs bg-gray-800">
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-right py-2 px-2">Cur Lim</th>
                    <th className="text-right py-2 px-2">New Lim</th>
                    <th className="text-right py-2 px-2">Cur Pre</th>
                    <th className="text-right py-2 px-2">New Pre</th>
                    <th className="text-left py-2 px-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const limChanged = r.department && Math.abs(Number(r.currentLim ?? 0) - Number(r.newLim ?? 0)) > 0.01
                    const preChanged = r.department && Math.abs(Number(r.currentPre ?? 0) - Number(r.newPre ?? 0)) > 0.01
                    const colour = r.error
                      ? 'text-red-400'
                      : (limChanged || preChanged) ? 'text-green-400' : 'text-gray-500'
                    return (
                      <tr key={i} className="border-b border-gray-800/50">
                        <td className="py-2 px-2 font-mono text-xs text-gray-300">{r.code}</td>
                        <td className="py-2 px-2">{r.error ? <span className="text-red-400 text-xs flex items-center gap-1"><AlertCircle size={10} /> {r.error}</span> : r.name}</td>
                        <td className="py-2 px-2 text-right text-gray-400">{fmtR(r.currentLim)}</td>
                        <td className="py-2 px-2 text-right text-white">{fmtR(r.newLim)}</td>
                        <td className="py-2 px-2 text-right text-gray-400">{fmtR(r.currentPre)}</td>
                        <td className="py-2 px-2 text-right text-white">{fmtR(r.newPre)}</td>
                        <td className={`py-2 px-2 text-xs ${colour}`}>{r.error ? '—' : changeSummary(r)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-800">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={rows.length === 0 || saving}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold"
          >
            {saving ? <><Loader size={14} className="animate-spin" /> Saving…</> : <>Save All Changes</>}
          </button>
        </div>
      </div>
    </div>
  )
}
